import { X } from "lucide-react";
import { useState, type KeyboardEvent, useRef } from "react";

type Props = {
  value?: string[];
  onChange?: (tags: string[]) => void;
  placeholder?: string;
  label?: string;
};

export const TagInput = ({
  value = [],
  onChange,
  placeholder = "Type and press Enter...",
  label = "Choices",
}: Props) => {
  const [tags, setTags] = useState<string[]>(value);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const updateTags = (newTags: string[]) => {
    setTags(newTags);
    onChange?.(newTags);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      if (!tags.includes(inputValue.trim())) {
        updateTags([...tags, inputValue.trim()]);
      }
      setInputValue("");
    }

    if (e.key === "Backspace" && !inputValue && tags.length > 0) {
      updateTags(tags.slice(0, -1));
    }
  };

  const removeTag = (indexToRemove: number) => {
    updateTags(tags.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="w-full">
      {label && <label className="mb-2 block text-sm font-medium">{label}</label>}

      <div
        className="flex min-h-[48px] w-full cursor-text flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-900"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="rounded-sm p-0.5 transition-colors hover:bg-blue-100"
              aria-label={`Remove ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="min-w-[120px] flex-1 bg-transparent text-sm outline-none"
        />
      </div>
    </div>
  );
};
